import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.parentElement.getBoundingClientRect();
    document.body.style.setProperty('--announcement-bar-height', `${height}px`);
  });

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.#resizeObserver.observe(this)
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }
  disconnectedCallback () {
    this.#resizeObserver.disconnect()
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get activeSlides() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    return (this.refs.slides ?? []).filter(slide => {
      if (isMobile && slide.classList.contains('announcement--hide-on-mobile')) return false;
      if (!isMobile && slide.classList.contains('announcement--hide-on-desktop')) return false;
      return true;
    });
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const activeSlides = this.activeSlides;
    const allSlides = this.refs.slides ?? [];

    if (activeSlides.length === 0) {
      allSlides.forEach(slide => slide.setAttribute('aria-hidden', 'true'));
      return;
    }

    let relativeIndex = current % activeSlides.length;
    if (relativeIndex < 0) {
      relativeIndex += activeSlides.length;
    }

    allSlides.forEach(slide => {
      slide.setAttribute('aria-hidden', `${activeSlides[relativeIndex] !== slide}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
